/**
 * Central enums for B2B entities.
 * Use these for event tracking, status transitions, and background job triggers.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Application
// ─────────────────────────────────────────────────────────────────────────────

export const APPLICATION_STATUSES = [
  'submitted',
  'viewed_by_client',
  'shortlisted',
  'interview_1_scheduled',
  'interview_1_conducted',
  'interview_2_scheduled',
  'interview_2_conducted',
  'offer_extended',
  'offer_accepted',
  'offer_rejected',
  'placed',
  'rejected',
  'no_show',
  'on_hold',
  'closed',
] as const;

export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Job
// ─────────────────────────────────────────────────────────────────────────────

export const JOB_STATUSES = ['open', 'on_hold', 'filled', 'closed'] as const;

export type JobStatus = (typeof JOB_STATUSES)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Requisition (client hiring request → may link to ggj_b2b_jobs)
// ─────────────────────────────────────────────────────────────────────────────

export const REQUISITION_STATUSES = [
  'draft',
  'awaiting_client',
  'submitted',
  'approved',
  'rejected',
  'sourcing',
  'filled',
  'on_hold',
  'cancelled',
] as const;

export type RequisitionStatus = (typeof REQUISITION_STATUSES)[number];

export const REQUISITION_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;

export type RequisitionPriority = (typeof REQUISITION_PRIORITIES)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Email / CTA
// ─────────────────────────────────────────────────────────────────────────────

export const CTA_ACTIONS = ['accept', 'reject', 'query'] as const;

export type CTAAction = (typeof CTA_ACTIONS)[number];

export const EMAIL_THREAD_LIFECYCLE = [
  'draft',
  'sent',
  'opened',
  'responded',
  'followup_stopped',
] as const;

export type EmailThreadLifecycle = (typeof EMAIL_THREAD_LIFECYCLE)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Workflow / Pipeline (Kanban columns map to ApplicationStatus)
// ─────────────────────────────────────────────────────────────────────────────

export const KANBAN_COLUMN_IDS = APPLICATION_STATUSES;

export type KanbanColumnId = ApplicationStatus;

// ─────────────────────────────────────────────────────────────────────────────
// Scheduler (follow-up emails)
// ─────────────────────────────────────────────────────────────────────────────

export const SCHEDULER_FOLLOWUP_STATES = ['pending', 'scheduled', 'sent', 'stopped'] as const;

export type SchedulerFollowupState = (typeof SCHEDULER_FOLLOWUP_STATES)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Calendar / Event
// ─────────────────────────────────────────────────────────────────────────────

export const CALENDAR_EVENT_TYPES = [
  'interview_1',
  'interview_2',
  'followup_due',
  'placement',
] as const;

export type CalendarEventType = (typeof CALENDAR_EVENT_TYPES)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Optimization (1:1, 1:n, bulk)
// ─────────────────────────────────────────────────────────────────────────────

export const OPTIMIZATION_TYPES = ['single', 'bulk'] as const;

export type OptimizationType = (typeof OPTIMIZATION_TYPES)[number];

export const OPTIMIZATION_STATUSES = ['pending', 'processing', 'completed', 'failed'] as const;

export type OptimizationStatus = (typeof OPTIMIZATION_STATUSES)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Activity / Audit (for event logging)
// ─────────────────────────────────────────────────────────────────────────────

export const ACTIVITY_ENTITIES = [
  'application',
  'candidate',
  'client',
  'job',
  'requisition',
  'email_thread',
  'optimization',
  'workflow',
  'scheduler',
  'calendar',
  'team',
  'org',
] as const;

export type ActivityEntity = (typeof ACTIVITY_ENTITIES)[number];

export const ACTIVITY_ACTIONS = [
  'created',
  'updated',
  'deleted',
  'status_changed',
  'assigned',
  'email_sent',
  'cta_responded',
  'optimization_started',
  'optimization_completed',
  'followup_scheduled',
  'followup_sent',
] as const;

export type ActivityAction = (typeof ACTIVITY_ACTIONS)[number];
